import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, RealtimeClient, call, createApi, type Api, type Session } from '@edil/api-client';
import { Skeleton, ToastProvider, useToast } from '@edil/ui';
import { DEFAULT_LANGUAGE, isLanguage, translate, type Language, type MessageKey } from '@edil/i18n';
import { API_URL, TIME_SYNC_MS, WS_URL } from '@/lib/config';
import { prefetchAwareFetch } from '@/lib/prefetch';
import { deviceId, loadSession, read, saveSession, write } from '@/lib/storage';
import { deepLinkPath, launchedInTelegram, loadTelegram, type TelegramWebApp } from '@/lib/telegram';
import { captureReferral } from '@/lib/referral';
import { useRouter } from '@/router';

export type Profile = Awaited<ReturnType<typeof fetchMe>>;

async function fetchMe(api: Api) {
  return call(api.GET('/v1/me'));
}
async function fetchWallet(api: Api) {
  return call(api.GET('/v1/wallet'));
}
export type Wallets = Awaited<ReturnType<typeof fetchWallet>>;
type ToastTone = 'info' | 'success' | 'error';

interface AppState {
  api: Api;
  lang: Language;
  setLang(l: Language): void;
  t(key: MessageKey, params?: Record<string, string | number>): string;
  me: Profile | null;
  /** Why the profile could not be loaded, while `me` is still null. */
  meError: ApiError | null;
  refreshMe(): Promise<void>;
  signedIn: boolean;
  signIn(tokens: { access_token: string; refresh_token: string }): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Inside the Mini App: signs in from Telegram's launch data, or opens sign-up for a new player.
   * False when it could not (launch data too old, or the API unreachable after retries).
   */
  telegramSignIn(): Promise<boolean>;
  /** Milliseconds to add to Date.now() to get the server clock. */
  clockOffset: number;
  realtime: RealtimeClient;
  live: boolean;
  telegram: TelegramWebApp | null;
  toast(text: string, tone?: ToastTone): void;
  ready: boolean;
  /** Balances, refreshed after purchases and on the player's realtime events. */
  wallet: Wallets | null;
  walletError: ApiError | null;
  refreshWallet(): Promise<void>;
  /** POINTS in FREE_PLAY mode, otherwise ETB. */
  currency: 'ETB' | 'POINTS';
  /** Fast Bingo is switched on for players (feature flag), so it shows in the menu. */
  bingoEnabled: boolean;
}

// Background retries while signed in but the profile or wallet is missing.
const HEAL_DELAYS_MS = [2_000, 5_000, 15_000];

function asApiError(e: unknown): ApiError {
  return e instanceof ApiError ? e : new ApiError(0, 'INTERNAL', String(e));
}

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

function initialLanguage(): Language {
  const saved = read('edil.lang');
  if (isLanguage(saved)) return saved;
  const nav = typeof navigator !== 'undefined' ? navigator.language.split('-')[0] : undefined;
  return isLanguage(nav) ? nav : DEFAULT_LANGUAGE;
}

export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <ToastProvider>
      <AppStateProvider>{children}</AppStateProvider>
    </ToastProvider>
  );
}

function AppStateProvider({ children }: { children: ReactNode }) {
  const pushToast = useToast();
  const [wallet, setWallet] = useState<Wallets | null>(null);
  const [currency, setCurrency] = useState<'ETB' | 'POINTS'>('ETB');
  const [bingoEnabled, setBingoEnabled] = useState(false);
  const sessionRef = useRef<Session | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [lang, setLangState] = useState<Language>(DEFAULT_LANGUAGE);
  const langRef = useRef<Language>(DEFAULT_LANGUAGE);
  const [me, setMe] = useState<Profile | null>(null);
  const [meError, setMeError] = useState<ApiError | null>(null);
  const [walletError, setWalletError] = useState<ApiError | null>(null);
  const [clockOffset, setClockOffset] = useState(0);
  const [live, setLive] = useState(false);
  const [telegram, setTelegram] = useState<TelegramWebApp | null>(null);
  const [ready, setReady] = useState(false);
  // Opened in Telegram without a session: hold the screens until we know whether this is a
  // returning player or a new one, so the home page or the website form never flashes first.
  const [holdForTelegram, setHoldForTelegram] = useState(() => launchedInTelegram() && !loadSession());

  const realtime = useMemo(
    () => new RealtimeClient({ url: WS_URL, getAccessToken: () => sessionRef.current?.accessToken ?? null, onConnectionChange: setLive }),
    [],
  );

  const setSession = useCallback(
    (s: Session | null) => {
      const tokenChanged = s?.accessToken !== sessionRef.current?.accessToken;
      sessionRef.current = s;
      saveSession(s);
      setSignedIn(Boolean(s));
      if (!s) {
        setMe(null);
        setWallet(null);
        setMeError(null);
        setWalletError(null);
      } else if (tokenChanged) {
        // Sign-in or a silent refresh: the socket still holds the old token for the user: channel.
        realtime.reauthenticate();
      }
    },
    [realtime],
  );

  const api = useMemo(
    () =>
      createApi({
        baseUrl: API_URL,
        getSession: () => sessionRef.current,
        setSession,
        getLanguage: () => langRef.current,
        deviceId: typeof window === 'undefined' ? 'ssr' : deviceId(),
        channel: launchedInTelegram() ? 'telegram' : 'web',
        fetch: prefetchAwareFetch as typeof fetch,
      }),
    [setSession],
  );

  const setLang = useCallback((l: Language) => {
    langRef.current = l;
    setLangState(l);
    write('edil.lang', l);
    document.documentElement.lang = l;
  }, []);

  const refreshMe = useCallback(async () => {
    if (!sessionRef.current) return setMe(null);
    try {
      const profile = await fetchMe(api);
      setMe(profile);
      setMeError(null);
    } catch (e) {
      // A failed refresh signs out (and clears this); anything else is shown with a retry.
      if (sessionRef.current) setMeError(asApiError(e));
    }
  }, [api]);

  const signIn = useCallback(
    async (tokens: { access_token: string; refresh_token: string }) => {
      setSession({ accessToken: tokens.access_token, refreshToken: tokens.refresh_token });
      await Promise.all([refreshMe(), refreshWallet()]);
    },
    [setSession, refreshMe],
  );

  const signingOut = useRef(false);
  const signOut = useCallback(async () => {
    signingOut.current = true; // not signed back in from Telegram: the player chose to leave
    await call(api.POST('/v1/auth/logout', {})).catch(() => {});
    setSession(null);
  }, [api, setSession]);

  const telegramRef = useRef<TelegramWebApp | null>(null);
  const router = useRouter();
  const telegramSignIn = useCallback(async (): Promise<boolean> => {
    const tg = telegramRef.current;
    if (!tg?.initData) return false;
    // A network blip or the API restarting (a deploy) is retried; a refusal (stale launch data,
    // rate limit) is not, and the sign-in screen offers the button again.
    for (const wait of [0, 1_000, 3_000]) {
      if (wait) await new Promise((r) => setTimeout(r, wait));
      try {
        const res = await call(api.POST('/v1/auth/telegram', { body: { init_data: tg.initData } }));
        signingOut.current = false;
        if (res.status === 'authenticated') await signIn(res);
        // Client-side navigation, never a page load: a reload would run the boot again and loop.
        else router.replace(`/register/?tg=${encodeURIComponent(res.registration_token)}&name=${encodeURIComponent(res.telegram.first_name)}`);
        return true;
      } catch (e) {
        const err = asApiError(e);
        if (err.status > 0 && err.status < 500) return false;
      }
    }
    return false;
  }, [api, signIn, router]);

  const toast = useCallback((text: string, tone: ToastTone = 'info') => pushToast(text, tone), [pushToast]);

  const refreshWallet = useCallback(async () => {
    if (!sessionRef.current) return setWallet(null);
    try {
      setWallet(await fetchWallet(api));
      setWalletError(null);
    } catch (e) {
      /* keep the last balance */
      if (sessionRef.current) setWalletError(asApiError(e));
    }
  }, [api]);

  // Start-up: language, session, Telegram, realtime, clock sync.
  useEffect(() => {
    setLang(initialLanguage());
    sessionRef.current = loadSession();
    setSignedIn(Boolean(sessionRef.current));
    realtime.start();
    // Invite links: remember the code; web opens are counted here, bot opens by the bot itself.
    const tapped = new URLSearchParams(window.location.search).get('mc');
    if (tapped) {
      void api.POST('/v1/marketing/clicks', { body: { id: tapped } }).catch(() => {});
      const url = new URL(window.location.href);
      url.searchParams.delete('mc');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
    const invited = captureReferral(window.location.search);
    if (invited && !launchedInTelegram()) void api.POST('/v1/referrals/visits', { body: { code: invited, channel: 'web' } }).catch(() => {});
    void (async () => {
      const tg = await loadTelegram();
      telegramRef.current = tg;
      setTelegram(tg);
      captureReferral('', tg?.initDataUnsafe.start_param);
      // Mini App sign-in from initData (§5.1); unknown users finish registration first.
      if (tg && !sessionRef.current && tg.initData) await telegramSignIn();
      setHoldForTelegram(false);
      const tgLang = tg?.initDataUnsafe.user?.language_code;
      if (!read('edil.lang') && isLanguage(tgLang)) setLang(tgLang);
      const link = deepLinkPath(tg?.initDataUnsafe.start_param);
      if (link && window.location.pathname === '/') window.location.replace(link);
      await Promise.all([refreshMe(), refreshWallet()]);
      call(api.GET('/v1/rooms'))
        .then((r) => setCurrency(r.currency))
        .catch(() => {});
      call(api.GET('/v1/bingo/rooms'))
        .then((r) => setBingoEnabled(r.enabled))
        .catch(() => {});
      setReady(true);
    })();
    return () => realtime.stop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Signed in but the profile or wallet failed to load (offline, flaky network): try again in the
  // background and whenever the app comes back to the foreground or online, instead of leaving
  // those screens on a skeleton.
  useEffect(() => {
    if (!ready || !signedIn || (me && wallet)) return;
    const heal = () => {
      if (document.visibilityState !== 'visible') return;
      if (!me) void refreshMe();
      if (!wallet) void refreshWallet();
    };
    const timers = HEAL_DELAYS_MS.map((ms) => setTimeout(heal, ms));
    document.addEventListener('visibilitychange', heal);
    window.addEventListener('online', heal);
    return () => {
      timers.forEach(clearTimeout);
      document.removeEventListener('visibilitychange', heal);
      window.removeEventListener('online', heal);
    };
  }, [ready, signedIn, Boolean(me), Boolean(wallet), refreshMe, refreshWallet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server clock (§5.5: countdown within 1 s of server time, re-synced every 60 s).
  useEffect(() => {
    const sync = async () => {
      const t0 = Date.now();
      try {
        const res = await call(api.GET('/v1/time'));
        const t1 = Date.now();
        setClockOffset(res.epoch_ms - Math.round((t0 + t1) / 2));
      } catch {
        /* keep the last offset */
      }
    };
    void sync();
    const id = setInterval(sync, TIME_SYNC_MS);
    return () => clearInterval(id);
  }, [api]);

  // The player's own events (purchases, wins, refunds) move the balance.
  useEffect(() => {
    if (!me) return;
    return realtime.subscribe(`user:${me.id}`, () => void refreshWallet());
  }, [me?.id, realtime, refreshWallet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the profile's language in step with the UI language.
  useEffect(() => {
    if (me && isLanguage(me.language) && !read('edil.lang')) setLang(me.language);
  }, [me, setLang]);

  // Inside Telegram a session that ends (expired, revoked elsewhere) is replaced straight away from
  // the launch data, so a Telegram player never lands on a password form they do not have.
  const wasSignedIn = useRef(false);
  const lastAutoSignIn = useRef(0);
  useEffect(() => {
    const lost = wasSignedIn.current && !signedIn;
    wasSignedIn.current = signedIn;
    // At most once a minute, so a session the API keeps refusing cannot loop.
    if (!lost || !ready || !telegram?.initData || signingOut.current || Date.now() - lastAutoSignIn.current < 60_000) return;
    lastAutoSignIn.current = Date.now();
    void telegramSignIn();
  }, [signedIn, ready, telegram, telegramSignIn]);

  const t = useCallback((key: MessageKey, params?: Record<string, string | number>) => translate(lang, key, params), [lang]);

  // Inside the Mini App, an account made on the website (phone and password) is linked to this
  // Telegram account as soon as the player signs in, so the bot can verify their number.
  const linkTried = useRef(false);
  useEffect(() => {
    if (!me || me.telegram_linked || !telegram?.initData || linkTried.current) return;
    linkTried.current = true;
    call(api.POST('/v1/me/telegram', { body: { init_data: telegram.initData } }))
      .then(async () => {
        await refreshMe();
        toast(t('profile.telegram_linked_done'), 'success');
      })
      .catch(() => {
        /* already linked to another account: the profile explains it */
      });
  }, [me?.id, me?.telegram_linked, telegram]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: AppState = { api, lang, setLang, t, me, meError, refreshMe, signedIn, signIn, signOut, telegramSignIn, clockOffset, realtime, live, telegram, toast, ready, wallet, walletError, refreshWallet, currency, bingoEnabled };
  return (
    <Ctx.Provider value={value}>
      {holdForTelegram ? (
        <div style={{ display: 'grid', gap: 12, padding: 16 }} aria-busy="true" data-testid="telegram-signing-in">
          <Skeleton height={48} radius={12} />
          <Skeleton height={180} radius={16} />
          <Skeleton height={120} radius={16} />
        </div>
      ) : (
        children
      )}
    </Ctx.Provider>
  );
}

/** Server-synced current time, updated every second. */
export function useServerNow(): number {
  const { clockOffset } = useApp();
  const [now, setNow] = useState(() => Date.now() + clockOffset);
  useEffect(() => {
    setNow(Date.now() + clockOffset);
    const id = setInterval(() => setNow(Date.now() + clockOffset), 1000);
    return () => clearInterval(id);
  }, [clockOffset]);
  return now;
}
